import { useEffect, useState } from "react";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, LoadingState } from "../components/states";
import { InboxList, IncidentGroups } from "../components/integrations";
import { api, type IncidentGroupView, type InboxItemView } from "../lib/api";

type Filter = "all" | "unread" | "read";

/**
 * Tenant notification inbox (section 34). INFO / SUCCESS / WARNING / ERROR /
 * ACTION_REQUIRED items with read / archive and grouped incidents. Summaries are
 * sanitized server-side and carry NO secrets and NO sensitive moderation
 * evidence — this view only renders the safe title / summary / action link.
 */
export function NotificationInbox(): JSX.Element {
  return (
    <IntegrationPage title="Notification inbox" intro={<p>Operational notifications for your workspace. Summaries never include secrets or sensitive moderation evidence.</p>}>
      {(tenantId) => <InboxPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function InboxPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [filter, setFilter] = useState<Filter>("all");
  const [items, setItems] = useState<InboxItemView[] | null>(null);
  const [incidents, setIncidents] = useState<IncidentGroupView[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    const read = filter === "unread" ? false : filter === "read" ? true : undefined;
    Promise.all([api.integrations.listInbox(tenantId, { read, limit: 50 }), api.integrations.listIncidents(tenantId)])
      .then(([r, inc]) => {
        setItems(r.items);
        setIncidents(inc.incidents);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads tenantId + filter it depends on
  }, [tenantId, filter]);

  const markRead = async (item: InboxItemView) => {
    setBusyId(item.id);
    try {
      await api.integrations.markInboxRead(tenantId, item.id);
      load();
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (item: InboxItemView) => {
    setBusyId(item.id);
    try {
      await api.integrations.archiveInbox(tenantId, item.id);
      load();
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const markAll = async () => {
    setMessage(null);
    try {
      const r = await api.integrations.markAllInboxRead(tenantId);
      setMessage(`Marked ${r.updated} notification(s) read.`);
      load();
    } catch (e) {
      setMessage(toMessage(e));
    }
  };

  return (
    <div className="ob-builder">
      <div className="row" style={{ gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
        <div className="row ob-tabs" role="tablist" aria-label="Inbox filter" style={{ gap: "0.5rem" }}>
          {(["all", "unread", "read"] as Filter[]).map((f) => (
            <button key={f} role="tab" aria-selected={filter === f} className={`btn ${filter === f ? "btn--primary" : "btn--secondary"}`} onClick={() => setFilter(f)}>
              {f}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--ghost" onClick={markAll}>
          Mark all read
        </button>
      </div>

      {message ? (
        <p className="muted" role="status" style={{ marginBottom: "0.5rem" }}>
          {message}
        </p>
      ) : null}

      {error ? (
        <ApiUnavailableState detail={error} />
      ) : items === null || incidents === null ? (
        <LoadingState label="Reading your inbox…" />
      ) : (
        <>
          {incidents.length > 0 ? (
            <div className="world-panel world-panel--proposed">
              <h4 className="section-title" style={{ marginTop: 0 }}>
                Grouped incidents
              </h4>
              <IncidentGroups incidents={incidents} />
            </div>
          ) : null}
          <InboxList items={items} onRead={markRead} onArchive={archive} busyId={busyId} />
        </>
      )}
    </div>
  );
}
